import { Box, Container, Stack } from '@mui/material';
import { ReactElement } from 'react';

import '@splidejs/react-splide/css';
import '@app/stories/slide.css';

import { CustomPagination } from '@app/components/Company/CompanyPagination';
import { FadeInUpItem } from '@app/components/Company/FadeInUpItem';
import { OrganizationDescription } from '@app/components/Company/OrganizationDescription';
import { OrganizationFeature } from '@app/components/Company/OrganizationFeature';
import { OrganizationHeader } from '@app/components/Company/OrganizationHeader';
import { OrganizationPointList } from '@app/components/Company/OrganizationPointList';
import { ProductShowingList } from '@app/components/Company/ProductShowingList';
import { Loading } from '@app/components/Shared/Loading';
import { useCompanyExhibitionContext } from '@app/hooks/companyExhibitionContext';
import { theme } from '@app/theme';
import { Organization } from '@app/types/organization';

interface OrganizationInfoProps {
  isPreview?: boolean;
  organization: Organization;
}

export function OrganizationInfo({
  isPreview = false,
  organization,
}: OrganizationInfoProps): ReactElement {
  const {
    exhibitionProducts,
    propertyProducts,
    exhibitionPrefecture,
    propertyPrefecture,
    setExhibitionPrefecture,
    setPropertyPrefecture,
    handlePaginationExhibitionProducts,
    handlePaginationPropertyProducts,
    isFetchingExhibitionProducts,
    isFetchingPropertyProducts,
    countLimit,
    totalRecordExhibition,
    totalRecordProperty,
  } = useCompanyExhibitionContext();

  return (
    <>
      {!organization ? (
        <Loading />
      ) : (
        <Container sx={{ pt: 3 }} maxWidth={false} disableGutters>
          <OrganizationHeader
            organization={organization}
            isPreview={isPreview}
          />
          <OrganizationFeature organization={organization} />
          <Stack justifyItems="center" id="model-house">
            {!isPreview && (
              <>
                <FadeInUpItem>
                  <Box bgcolor={theme.palette.neutral.grey1}>
                    <ProductShowingList
                      isFetching={isFetchingExhibitionProducts}
                      selectedPrefecture={exhibitionPrefecture}
                      setPrefecture={setExhibitionPrefecture}
                      products={exhibitionProducts ?? []}
                    />
                    <CustomPagination
                      sx={{ mt: 3, pb: 10 }}
                      handlePagination={handlePaginationExhibitionProducts}
                      limit={countLimit}
                      totalRecord={totalRecordExhibition}
                    />
                  </Box>
                </FadeInUpItem>
                <FadeInUpItem>
                  <Box bgcolor={theme.palette.neutral.grey1}>
                    <ProductShowingList
                      isProperty
                      isFetching={isFetchingPropertyProducts}
                      setPrefecture={setPropertyPrefecture}
                      selectedPrefecture={propertyPrefecture}
                      products={propertyProducts ?? []}
                    />
                    <CustomPagination
                      sx={{ mt: 3 }}
                      handlePagination={handlePaginationPropertyProducts}
                      limit={countLimit}
                      totalRecord={totalRecordProperty}
                    />
                  </Box>
                </FadeInUpItem>
              </>
            )}
            {!!organization.customFields?.strengths?.length && (
              <FadeInUpItem>
                <OrganizationPointList organization={organization} />
              </FadeInUpItem>
            )}
            <FadeInUpItem>
              <Stack
                bgcolor={theme.palette.neutral.grey1}
                sx={{
                  pt: 5,
                  px: {
                    sm: 1,
                    xs: 2,
                  },
                }}
              >
                <OrganizationDescription organization={organization} />
              </Stack>
            </FadeInUpItem>
          </Stack>
        </Container>
      )}
    </>
  );
}
